import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import {
  SectionCard,
  FieldLabel,
  TextInput,
  TextArea,
  PrimaryButton,
  SecondaryButton,
  AIBadge,
} from "@/components/ui-kit";
import AIAssistant from "@/components/AIAssistant";

const empty = { items: [] };

function chipClass(level) {
  const map = {
    Baja: "bg-[#F5F4F0] text-[#5C5B5A] border-[#E8E6E1]",
    Low: "bg-[#F5F4F0] text-[#5C5B5A] border-[#E8E6E1]",
    Media: "bg-[#F5EFE6] text-[#B89154] border-[#B89154]/30",
    Medium: "bg-[#F5EFE6] text-[#B89154] border-[#B89154]/30",
    Alta: "bg-[#9E2A2B]/10 text-[#9E2A2B] border-[#9E2A2B]/30",
    High: "bg-[#9E2A2B]/10 text-[#9E2A2B] border-[#9E2A2B]/30",
  };
  return map[level] || "bg-[#F5F4F0] text-[#5C5B5A] border-[#E8E6E1]";
}

export default function RisksModule({ data, project, onSave }) {
  const { t, lang } = useLang();
  const [v, setV] = useState({ ...empty, ...(data || {}) });

  useEffect(() => {
    setV((prev) => {
    const next = { ...empty, ...(data || {}) };
    try { if (JSON.stringify(prev) === JSON.stringify(next)) return prev; } catch (e) { void e; }
    return next;
  });
  }, [data]);
  useEffect(() => {
    onSave?.(v);
  }, [v, onSave]);

  const LEVELS = lang === "es" ? ["Baja", "Media", "Alta"] : ["Low", "Medium", "High"];

  const add = () =>
    setV((p) => ({
      ...p,
      items: [
        ...(p.items || []),
        { title: "", description: "", probability: LEVELS[1], impact: LEVELS[1], mitigation: "" },
      ],
    }));
  const update = (i, k, val) =>
    setV((p) => ({ ...p, items: p.items.map((x, idx) => (idx === i ? { ...x, [k]: val } : x)) }));
  const remove = (i) =>
    setV((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

  const suggestions = lang === "es"
    ? [
        "Identifica 6 riesgos clave para este negocio (regulatorios, mercado, operativos, financieros)",
        "Para cada riesgo propón un plan de mitigación concreto",
        "Lista los riesgos reputacionales más relevantes",
      ]
    : [
        "Identify 6 key risks (regulatory, market, operational, financial)",
        "Propose a concrete mitigation plan for each risk",
        "List the most relevant reputational risks",
      ];

  return (
    <>
      <SectionCard
        title={t("risk.title")}
        subtitle={lang === "es" ? "Anticipa lo que puede salir mal y cómo responderás." : "Anticipate what can go wrong and how you will respond."}
        action={
          <div className="flex items-center gap-3">
            <AIBadge />
            <AIAssistant module="risks" context={{ project: project?.name }} suggestions={suggestions} />
            <SecondaryButton onClick={add} testId="add-risk-btn">
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("risk.add")}
            </SecondaryButton>
          </div>
        }
        testId="risks-section"
      >
        {(v.items || []).length === 0 ? (
          <div className="text-sm text-[#8A8886]">—</div>
        ) : (
          <div className="space-y-3">
            {v.items.map((r, i) => (
              <div key={i} className="border border-[#E8E6E1] rounded-sm p-5" data-testid={`risk-${i}`}>
                <div className="grid md:grid-cols-[2fr_140px_140px] gap-3">
                  <div>
                    <FieldLabel>{t("risk.name")}</FieldLabel>
                    <TextInput value={r.title} onChange={(x) => update(i, "title", x)} testId={`risk-${i}-title`} />
                  </div>
                  <div>
                    <FieldLabel>{t("risk.probability")}</FieldLabel>
                    <select
                      value={r.probability}
                      onChange={(e) => update(i, "probability", e.target.value)}
                      className={`w-full border rounded-sm px-3 py-2 text-base font-medium ${chipClass(r.probability)}`}
                      data-testid={`risk-${i}-prob`}
                    >
                      {LEVELS.map((l) => (<option key={l} value={l}>{l}</option>))}
                    </select>
                  </div>
                  <div>
                    <FieldLabel>{t("risk.impact")}</FieldLabel>
                    <select
                      value={r.impact}
                      onChange={(e) => update(i, "impact", e.target.value)}
                      className={`w-full border rounded-sm px-3 py-2 text-base font-medium ${chipClass(r.impact)}`}
                      data-testid={`risk-${i}-impact`}
                    >
                      {LEVELS.map((l) => (<option key={l} value={l}>{l}</option>))}
                    </select>
                  </div>
                </div>
                <div className="mt-3">
                  <FieldLabel>{t("risk.description")}</FieldLabel>
                  <TextArea value={r.description} onChange={(x) => update(i, "description", x)} rows={2} testId={`risk-${i}-desc`} />
                </div>
                <div className="mt-3">
                  <FieldLabel hint={lang === "es" ? "Plan cuantitativo: % impacto, plan B, métrica" : "Quantitative plan: % impact, plan B, metric"}>{t("risk.mitigation")}</FieldLabel>
                  <TextArea value={r.mitigation} onChange={(x) => update(i, "mitigation", x)} rows={6} testId={`risk-${i}-mitigation`} />
                </div>
                <div className="mt-3 grid md:grid-cols-2 gap-3">
                  <div>
                    <FieldLabel hint={lang === "es" ? "Responsable del riesgo" : "Risk owner"}>{lang === "es" ? "Owner" : "Owner"}</FieldLabel>
                    <TextInput value={r.owner || ""} onChange={(x) => update(i, "owner", x)} placeholder="CEO + CFO" testId={`risk-${i}-owner`} />
                  </div>
                  <div>
                    <FieldLabel hint={lang === "es" ? "Señal que activa el plan" : "Signal that triggers the plan"}>{lang === "es" ? "Trigger" : "Trigger"}</FieldLabel>
                    <TextInput value={r.trigger || ""} onChange={(x) => update(i, "trigger", x)} placeholder={lang === "es" ? "Ej: CAC >€3.500 durante 2 meses" : "e.g. CAC >€3,500 for 2 months"} testId={`risk-${i}-trigger`} />
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <button onClick={() => remove(i)} className="text-[#8A8886] hover:text-[#9E2A2B] text-sm inline-flex items-center gap-1" data-testid={`risk-${i}-remove`}>
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} /> {t("common.delete")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

    </>
  );
}
